import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { InviteDialog } from "@/components/InviteDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ManagedUser {
  id: string;
  email: string | null;
  created_at?: string;
  last_sign_in_at?: string | null;
  role?: string;
}

export function UsersManagement() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-users", { page, perPage, search }],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("manage-users", {
        body: { action: "list", page, perPage, search },
      });
      if (error) throw error;
      return data as { users: ManagedUser[]; total: number };
    },
  });

  const resendInvite = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.functions.invoke("manage-users", {
        body: { action: "resend_invite", email },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Invite sent", description: "Invitation email has been resent." });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to resend", description: e.message }),
  });

  const resetPassword = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.functions.invoke("manage-users", {
        body: { action: "reset_password", email },
      });
      if (error) throw error;
    },
    onSuccess: () => toast({ title: "Reset email sent", description: "Password reset email has been sent." }),
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to send reset", description: e.message }),
  });

  const deleteUser = useMutation({
    mutationFn: async (user_id: string) => {
      const { error } = await supabase.functions.invoke("manage-users", {
        body: { action: "delete", user_id },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "User deleted" });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to delete", description: e.message }),
  });

  const users = data?.users || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle>Users Management</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-64"
            />
            <InviteDialog>
              <Button>Invite users</Button>
            </InviteDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4">
          <AlertDescription>
            Only the admin can access this tool. Resend invites, send password resets, or delete accounts.
          </AlertDescription>
        </Alert>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last sign-in</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading users…</TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>No users found.</TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</TableCell>
                    <TableCell>{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "Never"}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={() => resendInvite.mutate(u.email || "")} disabled={!u.email || resendInvite.isPending}>
                        Resend invite
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => resetPassword.mutate(u.email || "")} disabled={!u.email || resetPassword.isPending}>
                        Reset password
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => deleteUser.mutate(u.id)} disabled={deleteUser.isPending}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
